import { Button, Input, InputGroup, Label, Textarea } from '@monksflow/monks-ui';
import { XIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  ImageSourcePicker,
  type ImageSource,
  type ImageSourcePickerValue,
  type UseKnowledgeBaseImagesHook,
} from '@/shared/ui/ImageSourcePicker';
import type { CustomAttribute } from '../../model/customAttributes';
import { CustomAttributesEditor } from './CustomAttributesEditor';
import type { ConfigureElementTarget } from './ConfigureContentElementSheet.types';

/** Image tabs offered here, in tab order — the first is also the tab the picker opens on. */
const IMAGE_SOURCES: ImageSource[] = ['upload', 'kb', 'url'];

export interface ConfigureContentElementFieldsProps {
  target: ConfigureElementTarget;
  isImage: boolean;
  canEditContent: boolean;
  sectionName: string;
  setSectionName: (value: string) => void;
  slugError: string | null;
  content: string;
  setContent: (value: string) => void;
  setImageValue: (value: ImageSourcePickerValue | null) => void;
  imageDescription: string;
  setImageDescription: (value: string) => void;
  attributes: CustomAttribute[];
  setAttributes: (attributes: CustomAttribute[]) => void;
  useKnowledgeBaseImagesHook?: UseKnowledgeBaseImagesHook;
}

/**
 * Body fields for {@link useConfigureContentElementForm}, shared verbatim by the Dialog and Sheet
 * shells. Pure presentational — every value and setter comes from the hook.
 */
export function ConfigureContentElementFields({
  target,
  isImage,
  canEditContent,
  sectionName,
  setSectionName,
  slugError,
  content,
  setContent,
  setImageValue,
  imageDescription,
  setImageDescription,
  attributes,
  setAttributes,
  useKnowledgeBaseImagesHook,
}: ConfigureContentElementFieldsProps) {
  const { t } = useTranslation('admin');

  return (
    <>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="configure-element-selector">
          {t('templatesConfig.configureElement.elementSelector')}
        </Label>
        <Input id="configure-element-selector" value={target.tagName} readOnly disabled />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="configure-element-section">
          {t('templatesConfig.configureElement.sectionName')}
        </Label>
        {/* The group draws the border and carries `has-[input[aria-invalid=true]]`, so the taken-name
            styling survives the wrap. The clear button is a sibling of the input, not an
            `InputDecoration` — that slot sets `pointer-events-none` on its svg. */}
        <InputGroup>
          <Input
            id="configure-element-section"
            value={sectionName}
            onChange={(event) => setSectionName(event.target.value)}
            placeholder={t('templatesConfig.configureElement.sectionNamePlaceholder')}
            aria-invalid={!!slugError || undefined}
            // eslint-disable-next-line jsx-a11y/no-autofocus -- the field the modal exists to fill
            autoFocus
          />
          {sectionName && (
            // Blanking the name IS the untag; this just makes it one click instead of
            // select-all-delete, and keeps it in the field so it cannot read as deletion.
            <Button
              variant="ghost"
              size="mini"
              aria-label={t('templatesConfig.configureElement.clearSectionName')}
              onClick={() => setSectionName('')}
            >
              <XIcon className="size-4" />
            </Button>
          )}
        </InputGroup>
        {slugError ? (
          <p className="text-xs text-destructive">{slugError}</p>
        ) : (
          <p className="text-xs text-muted-foreground">
            {t('templatesConfig.configureElement.sectionNameHelp')}
          </p>
        )}
      </div>

      {canEditContent && (
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="configure-element-content">
            {target.hasChildElements
              ? t('templatesConfig.configureElement.contentHtml')
              : t('templatesConfig.configureElement.textContent')}
          </Label>
          {/* `max-h-64`, not `rows`: Textarea ships `field-sizing-content`, which overrides `rows`
              and grows the field to fit its value. A max height is the only thing that actually
              bounds it. */}
          <Textarea
            id="configure-element-content"
            value={content}
            onChange={(event) => setContent(event.target.value)}
            className="max-h-64"
          />
        </div>
      )}

      {isImage && (
        <>
          <ImageSourcePicker
            sources={IMAGE_SOURCES}
            onChange={setImageValue}
            useKnowledgeBaseImagesHook={useKnowledgeBaseImagesHook}
          />
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="configure-element-description">
              {t('templatesConfig.configureElement.imageDescription')}
            </Label>
            <Textarea
              id="configure-element-description"
              value={imageDescription}
              onChange={(event) => setImageDescription(event.target.value)}
              placeholder={t('templatesConfig.configureElement.imageDescriptionPlaceholder')}
              rows={3}
            />
            <p className="text-xs text-muted-foreground">
              {t('templatesConfig.configureElement.imageDescriptionHelp')}
            </p>
          </div>
        </>
      )}

      <CustomAttributesEditor attributes={attributes} onChange={setAttributes} />
    </>
  );
}
